import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import {
  createIntegrationSupportSession,
  pingIntegrationSupportSession,
  streamIntegrationSupportMessage,
} from "../api/services/integrationSupportAgentService";
import {
  clearStoredAgentSession,
  getStoredAgentSession,
  setStoredAgentSession,
} from "../lib/agentSessionStorage";
import type { AgentContextUsage } from "../types/agentChat";

export interface IntegrationSupportChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: Date;
}

const WELCOME =
  "Integration Support Analyst is ready. I can help you discover your subscribed products, build integrations, review your code, and assess certification readiness.";

export function useIntegrationSupportAgentChat() {
  const { activeTenantId } = useAuth();
  const [messages, setMessages] = useState<IntegrationSupportChatMessage[]>([
    {
      id: "welcome",
      role: "assistant",
      content: WELCOME,
      timestamp: new Date(),
    },
  ]);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [contextUsage, setContextUsage] = useState<AgentContextUsage | null>(null);
  const [sessionReady, setSessionReady] = useState(false);
  const tenantRef = useRef(activeTenantId);

  useEffect(() => {
    if (tenantRef.current && tenantRef.current !== activeTenantId) {
      clearStoredAgentSession("integration-support", tenantRef.current);
      setSessionId(null);
      setSessionReady(false);
      setContextUsage(null);
    }
    tenantRef.current = activeTenantId;
  }, [activeTenantId]);

  useEffect(() => {
    let cancelled = false;
    async function restoreSession() {
      if (!activeTenantId) {
        setSessionId(null);
        setSessionReady(true);
        return;
      }
      const stored = getStoredAgentSession("integration-support", activeTenantId);
      if (!stored?.sessionId) {
        setSessionId(null);
        setSessionReady(true);
        return;
      }
      try {
        const valid = await pingIntegrationSupportSession(stored.sessionId);
        if (cancelled) return;
        if (valid) {
          setSessionId(stored.sessionId);
        } else {
          clearStoredAgentSession("integration-support", activeTenantId);
          setSessionId(null);
        }
      } catch {
        if (!cancelled) {
          clearStoredAgentSession("integration-support", activeTenantId);
          setSessionId(null);
        }
      } finally {
        if (!cancelled) setSessionReady(true);
      }
    }
    restoreSession();
    return () => {
      cancelled = true;
    };
  }, [activeTenantId]);

  const ensureSession = useCallback(async (): Promise<string> => {
    if (sessionId) return sessionId;
    const id = await createIntegrationSupportSession();
    setSessionId(id);
    if (activeTenantId) {
      setStoredAgentSession("integration-support", activeTenantId, id);
    }
    return id;
  }, [sessionId, activeTenantId]);

  const sendMessage = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed) return;
      setError(null);
      setLoading(true);
      const userMsg: IntegrationSupportChatMessage = {
        id: `user-${Date.now()}`,
        role: "user",
        content: trimmed,
        timestamp: new Date(),
      };
      const assistantId = `assistant-${Date.now()}`;
      const assistantMsg: IntegrationSupportChatMessage = {
        id: assistantId,
        role: "assistant",
        content: "",
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, userMsg, assistantMsg]);
      try {
        const sid = await ensureSession();
        const result = await streamIntegrationSupportMessage(sid, trimmed, {
          onTextDelta: (delta) => {
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantId
                  ? { ...m, content: m.content + delta }
                  : m,
              ),
            );
          },
        });
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantId
              ? { ...m, content: result.response }
              : m,
          ),
        );
        setContextUsage(result.context_usage ?? null);
        return result.response;
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : "Failed to send message");
        setMessages((prev) => {
          const assistant = prev.find((m) => m.id === assistantId);
          if (assistant && !assistant.content.trim()) {
            return prev.filter((m) => m.id !== assistantId);
          }
          return prev;
        });
        return null;
      } finally {
        setLoading(false);
      }
    },
    [ensureSession],
  );

  return {
    messages,
    loading,
    error,
    contextUsage,
    sessionReady,
    sessionId,
    sendMessage,
  };
}
